import { RefObject, useCallback } from 'react';
import { captureException } from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';

type AlertType = 'error' | 'warning' | 'info' | 'success';

type GeneratePdfOptions = {
  componentRef: RefObject<HTMLElement>;
  onLoadingChange?: (loading: boolean) => void;
  onError?: (message: string, type?: AlertType) => void;
  fileName?: string;
};

type BlockBounds = {
  top: number;
  bottom: number;
};

type PageSlice = {
  startPx: number;
  endPx: number;
};

const NO_SPLIT_BLOCK_SELECTOR =
  '.print-question-block, .question-block-print-inline, .question-row, .print-footer-block';
const FOOTER_SELECTOR = '.print-footer-block';
const SAFETY_MARGIN_PX = 4;
const MIN_PAGE_FILL_RATIO = 0.15;
const FOOTER_GAP_MM = 2;
const CONTENT_TOP_MARGIN_MM = 10;

const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSByUa = /iPad|iPhone|iPod/.test(ua);
  const iPadOs13Plus =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSByUa || iPadOs13Plus;
};

const getBlockBounds = (
  blockNodes: HTMLElement[],
  containerTop: number,
  renderScale: number
): BlockBounds[] =>
  blockNodes
    .map((el) => {
      const rect = el.getBoundingClientRect();
      return {
        top: (rect.top - containerTop) * renderScale,
        bottom: (rect.bottom - containerTop) * renderScale
      };
    })
    .filter((b) => b.bottom > b.top)
    .sort((a, b) => a.top - b.top);

const getBlockBoundsInDomPx = (
  blockNodes: HTMLElement[],
  containerTop: number
): BlockBounds[] =>
  blockNodes
    .map((el) => {
      const rect = el.getBoundingClientRect();
      return {
        top: rect.top - containerTop,
        bottom: rect.bottom - containerTop
      };
    })
    .filter((b) => b.bottom > b.top)
    .sort((a, b) => a.top - b.top);

const computePageSlices = (
  totalCanvasHeight: number,
  pageHeightInCanvasPx: number | ((pageIndex: number) => number),
  blocks: BlockBounds[]
): PageSlice[] => {
  const slices: PageSlice[] = [];
  let currentTop = 0;
  let pageIndex = 0;
  const getPageHeight = (idx: number): number =>
    typeof pageHeightInCanvasPx === 'function'
      ? pageHeightInCanvasPx(idx)
      : pageHeightInCanvasPx;

  while (currentTop < totalCanvasHeight) {
    const currentPageHeight = Math.max(1, getPageHeight(pageIndex));
    let proposedEnd = Math.min(
      currentTop + currentPageHeight,
      totalCanvasHeight
    );

    if (proposedEnd < totalCanvasHeight && blocks.length > 0) {
      const conflicting = blocks.find(
        (b) =>
          b.top > currentTop + SAFETY_MARGIN_PX &&
          b.top < proposedEnd &&
          b.bottom > proposedEnd
      );

      if (conflicting) {
        const breakAt = Math.floor(conflicting.top - SAFETY_MARGIN_PX);
        const minViableEnd =
          currentTop + currentPageHeight * MIN_PAGE_FILL_RATIO;
        if (breakAt > minViableEnd) {
          proposedEnd = breakAt;
        }
      }
    }

    if (proposedEnd <= currentTop) {
      proposedEnd = Math.min(
        currentTop + currentPageHeight,
        totalCanvasHeight
      );
    }

    slices.push({ startPx: currentTop, endPx: proposedEnd });
    currentTop = proposedEnd;
    pageIndex += 1;
  }

  return slices;
};

const sliceCanvas = (
  sourceCanvas: HTMLCanvasElement,
  startPx: number,
  height: number
): string | null => {
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = sourceCanvas.width;
  pageCanvas.height = height;
  const ctx = pageCanvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  ctx.drawImage(
    sourceCanvas,
    0,
    startPx,
    sourceCanvas.width,
    height,
    0,
    0,
    sourceCanvas.width,
    height
  );
  return pageCanvas.toDataURL('image/png');
};

const usePdfFromRef = () => {
  const showAlert = useAlert();
  const openPdfInNewTabFromRef = useCallback(async ({
    componentRef,
    onLoadingChange,
    onError,
    fileName = 'document.pdf'
  }: GeneratePdfOptions) => {
    if (!componentRef?.current) {
      onError?.('Unable to generate PDF. Please try again.', 'error');
      return;
    }

    onLoadingChange?.(true);
    let tempWrapper: HTMLDivElement | null = null;

    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const renderScale = isIOSDevice() ? 1 : 2;

      tempWrapper = document.createElement('div');
      tempWrapper.style.position = 'fixed';
      tempWrapper.style.left = '-10000px';
      tempWrapper.style.top = '0';
      tempWrapper.style.width = `${componentRef.current.scrollWidth || 1200}px`;
      tempWrapper.style.zIndex = '0';
      tempWrapper.style.opacity = '0';
      tempWrapper.style.pointerEvents = 'none';
      tempWrapper.style.background = '#ffffff';

      const clonedContent = componentRef.current.cloneNode(true) as HTMLElement;
      clonedContent.style.display = 'block';
      const captureViewport = document.createElement('div');
      captureViewport.style.position = 'relative';
      captureViewport.style.width = `${componentRef.current.scrollWidth || 1200}px`;
      captureViewport.style.overflow = 'hidden';
      captureViewport.style.background = '#ffffff';
      captureViewport.appendChild(clonedContent);
      tempWrapper.appendChild(captureViewport);
      document.body.appendChild(tempWrapper);

      await new Promise((resolve) => setTimeout(resolve, 150));

      const canvas = await html2canvas(clonedContent, {
        useCORS: true,
        backgroundColor: '#ffffff',
        scale: renderScale,
        logging: false
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const contentWidthDomPx = clonedContent.scrollWidth || canvas.width / renderScale;
      const contentHeightDomPx = clonedContent.scrollHeight || canvas.height / renderScale;
      const pageHeightDomPx = (pdfHeight * contentWidthDomPx) / pdfWidth;
      const pxPerMm = (contentWidthDomPx * renderScale) / pdfWidth;
      const domPxPerMm = contentWidthDomPx / pdfWidth;

      const containerTop = clonedContent.getBoundingClientRect().top;
      const blockNodes = Array.from(
        clonedContent.querySelectorAll<HTMLElement>(NO_SPLIT_BLOCK_SELECTOR)
      );

      const footerEl = clonedContent.querySelector<HTMLElement>(FOOTER_SELECTOR);
      let footerTopDomPx = contentHeightDomPx;
      let footerHeightDomPx = 0;
      if (footerEl) {
        const footerRect = footerEl.getBoundingClientRect();
        const footerTop = footerRect.top - containerTop;
        const footerBottom = footerRect.bottom - containerTop;
        if (footerBottom > footerTop && footerTop < contentHeightDomPx) {
          footerTopDomPx = Math.max(0, footerTop);
          footerHeightDomPx = Math.min(contentHeightDomPx, footerBottom) - footerTopDomPx;
        }
      }
      const hasFooter = footerHeightDomPx > 0;

      let lastSliceHeightMm = 0;

      const topMarginDomPx = CONTENT_TOP_MARGIN_MM * domPxPerMm;
      const topMarginCanvasPx = CONTENT_TOP_MARGIN_MM * pxPerMm;
      const getSliceTopOffsetMm = (pageIndex: number): number =>
        pageIndex === 0 ? 0 : CONTENT_TOP_MARGIN_MM;
      let lastPageIndex = 0;

      if (isIOSDevice()) {
        const blocksDomPx = getBlockBoundsInDomPx(blockNodes, containerTop);
        const effectiveContentHeightDomPx = hasFooter
          ? footerTopDomPx
          : contentHeightDomPx;
        const getPageHeightDomPx = (idx: number): number =>
          idx === 0
            ? pageHeightDomPx
            : Math.max(1, pageHeightDomPx - topMarginDomPx);
        const pageSlicesDomPx = computePageSlices(
          effectiveContentHeightDomPx,
          getPageHeightDomPx,
          blocksDomPx
        );

        for (let pageIndex = 0; pageIndex < pageSlicesDomPx.length; pageIndex += 1) {
          const slice = pageSlicesDomPx[pageIndex];
          const sliceHeightDomPx = slice.endPx - slice.startPx;
          if (sliceHeightDomPx <= 0) continue;

          captureViewport.style.height = `${sliceHeightDomPx}px`;
          clonedContent.style.transform = `translate3d(0, ${-slice.startPx}px, 0)`;
          clonedContent.style.transformOrigin = 'top left';
          await new Promise((resolve) =>
            requestAnimationFrame(() => resolve(null))
          );

          const pageCanvas = await html2canvas(captureViewport, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: renderScale,
            logging: false,
            width: contentWidthDomPx,
            height: sliceHeightDomPx,
            windowWidth: contentWidthDomPx,
            windowHeight: sliceHeightDomPx
          });

          const sliceImg = pageCanvas.toDataURL('image/png');
          const sliceHeightMm = sliceHeightDomPx / domPxPerMm;
          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(
            sliceImg,
            'PNG',
            0,
            getSliceTopOffsetMm(pageIndex),
            pdfWidth,
            sliceHeightMm
          );
          lastSliceHeightMm = sliceHeightMm;
          lastPageIndex = pageIndex;
        }

        if (hasFooter) {
          captureViewport.style.height = `${footerHeightDomPx}px`;
          clonedContent.style.transform = `translate3d(0, ${-footerTopDomPx}px, 0)`;
          clonedContent.style.transformOrigin = 'top left';
          await new Promise((resolve) =>
            requestAnimationFrame(() => resolve(null))
          );

          const footerCanvas = await html2canvas(captureViewport, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: renderScale,
            logging: false,
            width: contentWidthDomPx,
            height: footerHeightDomPx,
            windowWidth: contentWidthDomPx,
            windowHeight: footerHeightDomPx
          });

          const footerImg = footerCanvas.toDataURL('image/png');
          const footerHeightMm = footerHeightDomPx / domPxPerMm;
          const usedSpaceMm =
            getSliceTopOffsetMm(lastPageIndex) + lastSliceHeightMm;
          const remainingSpaceMm = pdfHeight - usedSpaceMm - FOOTER_GAP_MM;
          if (pageSlicesDomPx.length === 0 || footerHeightMm > remainingSpaceMm) {
            if (pageSlicesDomPx.length > 0) {
              pdf.addPage();
            }
          }
          pdf.addImage(
            footerImg,
            'PNG',
            0,
            pdfHeight - footerHeightMm,
            pdfWidth,
            footerHeightMm
          );
        }

        clonedContent.style.transform = '';
        captureViewport.style.height = '';
      } else {
        const fullPageHeightInCanvasPx = pdfHeight * pxPerMm;
        const reducedPageHeightInCanvasPx = Math.max(
          1,
          fullPageHeightInCanvasPx - topMarginCanvasPx
        );
        const getPageHeightInCanvasPx = (idx: number): number =>
          idx === 0 ? fullPageHeightInCanvasPx : reducedPageHeightInCanvasPx;
        const blocks = getBlockBounds(blockNodes, containerTop, renderScale);
        const footerStartCanvasPx = hasFooter
          ? Math.max(0, footerTopDomPx * renderScale)
          : canvas.height;
        const effectiveCanvasHeight = hasFooter ? footerStartCanvasPx : canvas.height;

        const pageSlices = computePageSlices(
          effectiveCanvasHeight,
          getPageHeightInCanvasPx,
          blocks
        );

        pageSlices.forEach((slice, pageIndex) => {
          const sliceHeight = slice.endPx - slice.startPx;
          if (sliceHeight <= 0) {
            return;
          }
          const sliceImg = sliceCanvas(canvas, slice.startPx, sliceHeight);
          if (!sliceImg) {
            return;
          }
          const sliceHeightMm = sliceHeight / pxPerMm;
          if (pageIndex > 0) {
            pdf.addPage();
          }
          pdf.addImage(
            sliceImg,
            'PNG',
            0,
            getSliceTopOffsetMm(pageIndex),
            pdfWidth,
            sliceHeightMm
          );
          lastSliceHeightMm = sliceHeightMm;
          lastPageIndex = pageIndex;
        });

        if (hasFooter) {
          const footerHeightCanvasPx = canvas.height - footerStartCanvasPx;
          const footerImg = sliceCanvas(
            canvas,
            footerStartCanvasPx,
            footerHeightCanvasPx
          );
          if (footerImg) {
            const footerHeightMm = footerHeightCanvasPx / pxPerMm;
            const usedSpaceMm =
              getSliceTopOffsetMm(lastPageIndex) + lastSliceHeightMm;
            const remainingSpaceMm = pdfHeight - usedSpaceMm - FOOTER_GAP_MM;
            if (pageSlices.length === 0 || footerHeightMm > remainingSpaceMm) {
              if (pageSlices.length > 0) {
                pdf.addPage();
              }
            }
            pdf.addImage(
              footerImg,
              'PNG',
              0,
              pdfHeight - footerHeightMm,
              pdfWidth,
              footerHeightMm
            );
          }
        }
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000);
      showAlert('PDF generated successfully and downloaded', 'success');
    } catch (error) {
      captureException(error);
      onError?.('Failed to generate PDF. Please try again.', 'error');
    } finally {
      if (tempWrapper && document.body.contains(tempWrapper)) {
        document.body.removeChild(tempWrapper);
      }
      onLoadingChange?.(false);
    }
  },
  [showAlert]
);

  return { openPdfInNewTabFromRef };
};

export default usePdfFromRef;
